import type jsPDF from 'jspdf';
import type { ImageFormat as JsPDFImageFormat, Matrix } from 'jspdf';

import type { PDFLinks } from './links';
import { setPDFTextFont } from './utils';
import type {
  ClipRect,
  DrawImageInstruction,
  DrawInstruction,
  DrawLineInstruction,
  DrawRectInstruction,
  DrawTextInstruction,
  TransformationMatrix,
} from '../../../rendering';
import type { ImageFormat, ImageMap } from '../../../types';
import { assertNeverSafely } from '../../../util';
import { toHex } from '../../../util/color';
import { blackColor } from '../../helpers';

/** jsPDF picks its decoder by name, and its GIF89a decoder handles GIF87a as well. */
const JSPDF_IMAGE_FORMATS: Record<ImageFormat, JsPDFImageFormat> = {
  bmp: 'BMP',
  gif: 'GIF89a',
  jpeg: 'JPEG',
  png: 'PNG',
  webp: 'WEBP',
};

export class PDFRenderer {
  private pdf: jsPDF;
  private pageNumber: number;
  private pageHeight: number;
  private imageMap: ImageMap;
  private links: PDFLinks;

  constructor(options: {
    imageMap: ImageMap;
    links: PDFLinks;
    pageHeight: number;
    pageNumber: number;
    pdf: jsPDF;
  }) {
    const { imageMap, links, pageHeight, pageNumber, pdf } = options;
    this.pdf = pdf;
    this.pageNumber = pageNumber;
    this.pageHeight = pageHeight;
    this.imageMap = imageMap;
    this.links = links;
  }

  draw(drawInstruction: DrawInstruction) {
    switch (drawInstruction.type) {
      case 'text':
        this.drawText(drawInstruction);
        break;

      case 'rect':
        this.drawRect(drawInstruction);
        break;

      case 'line':
        this.drawLine(drawInstruction);
        break;

      case 'image':
        this.drawImage(drawInstruction);
        break;

      case 'pdfLink':
        this.links.addLink(drawInstruction, { pageNumber: this.pageNumber });
        break;

      case 'pdfBookmark':
        this.links.addBookmark(drawInstruction, { pageNumber: this.pageNumber });
        break;

      // Ignore draw instructions that are not relevant to PDF generation
      case 'caret':
      case 'annotationMarker':
      case 'reactComponent':
      case 'error':
        break;

      default:
        assertNeverSafely(drawInstruction);
    }
  }

  // Draw methods
  private drawText({ clipRect, color, font, text, x, y }: DrawTextInstruction) {
    this.saveAndRestore(() => {
      this.applyClipRect(clipRect);
      setPDFTextFont(this.pdf, font);
      const fontColorHex = toHex(color) ?? blackColor;
      this.pdf.setTextColor(fontColorHex);
      this.pdf.text(text, x, y);
    });
  }

  private drawRect({
    clipRect,
    cornerRadius = 0,
    fillColor,
    height,
    stroke,
    width,
    x,
    y,
  }: DrawRectInstruction) {
    this.saveAndRestore(() => {
      this.applyClipRect(clipRect);

      const fillColorHex = fillColor && toHex(fillColor);
      const hasFill = !!fillColorHex;

      const strokeColorHex = stroke?.color && toHex(stroke.color);
      const strokeThickness = stroke?.thickness ?? 0;
      const hasStroke = !!strokeColorHex;

      if (!getIsFiniteRect({ cornerRadius, height, width, x, y })) return;
      if (!hasFill && !hasStroke) return;

      if (hasFill) this.pdf.setFillColor(fillColorHex);
      if (hasStroke) {
        this.pdf.setDrawColor(strokeColorHex);
        this.pdf.setLineWidth(strokeThickness);
      }

      const styleMode = hasFill && hasStroke ? 'DF' : hasFill ? 'F' : hasStroke ? 'S' : '';
      this.pdf.roundedRect(x, y, width, height, cornerRadius, cornerRadius, styleMode);
    });
  }

  private drawLine({
    clipRect,
    color,
    dashPattern,
    endX,
    endY,
    lineCap,
    startX,
    startY,
    thickness,
    transform,
  }: DrawLineInstruction) {
    this.saveAndRestore(() => {
      this.applyTransform(transform);
      this.applyClipRect(clipRect);

      const colorHex = toHex(color);
      if (!colorHex) return;

      this.pdf.setDrawColor(colorHex);
      this.pdf.setLineWidth(thickness);
      if (lineCap) this.pdf.setLineCap(lineCap);
      if (dashPattern) this.pdf.setLineDashPattern(dashPattern, 0);

      this.pdf.line(startX, startY, endX, endY);
    });
  }

  private drawImage({
    clipRect,
    height,
    imageId,
    opacity,
    transform,
    width,
    x,
    y,
  }: DrawImageInstruction) {
    const imageAsset = this.imageMap.get(imageId);
    if (!imageAsset) return;

    if (!imageAsset.format) {
      console.error(`Skipping image ${imageId}: not an embeddable image format.`);
      return;
    }
    const format = JSPDF_IMAGE_FORMATS[imageAsset.format];

    this.saveAndRestore(() => {
      this.applyClipRect(clipRect);
      this.applyTransform(transform);

      if (opacity) this.pdf.setGState(this.pdf.GState({ opacity }));

      try {
        this.pdf.addImage(imageAsset.base64, format, x, y, width, height);
      } catch (error) {
        /**
         * In some rare cases, the image is corrupted and cannot be added to the PDF. Fail
         * gracefully by skipping the image.
         */
        console.error('Error adding image to PDF:', error);
      }
    });
  }

  // Helpers
  private saveAndRestore(fn: () => void) {
    this.pdf.saveGraphicsState();
    fn();
    this.pdf.restoreGraphicsState();
  }

  /**
   * Apply a transformation matrix to the PDF.
   *
   * Transformation matrices specified on draw instructions use the same coordinate system as the
   * page (the top-left corner of the page is (0, 0) and the bottom-right corner is
   * (pageWidth, pageHeight)). However, setCurrentTransformationMatrix expects the provided matrix
   * to use the PDF's internal coordinate system. In this coordinate system, (0, 0) is the
   * bottom-left corner of the page, and the top-right corner of the page is
   * (pageWidth * scaleFactor, pageHeight * scaleFactor).
   *
   * For this reason, we must convert the given matrix to use the correct coordinate system by
   * bracketing it with a pair of simple transforms that convert input coordinates from the internal
   * coordinate system to the page coordinate system and back again.
   */
  private applyTransform(transform: TransformationMatrix | undefined) {
    if (!transform) return;
    const [a, b, c, d, e, f] = transform;
    const { scaleFactor } = this.pdf.internal;

    const matrix: Matrix = this.pdf.Matrix(a, b, c, d, e, f);

    const convertPageToInteralCoords: Matrix = this.pdf.Matrix(
      scaleFactor, // Scale X
      0,
      0,
      -scaleFactor, // Flip and scale Y
      0,
      this.pageHeight * scaleFactor // Translate up to account for flipping
    );

    const convertInternalToPageCoords = convertPageToInteralCoords.inversed();

    const convertedMatrix = convertPageToInteralCoords
      .multiply(matrix)
      .multiply(convertInternalToPageCoords);

    this.pdf.setCurrentTransformationMatrix(convertedMatrix);
  }

  private applyClipRect(clipRect: ClipRect | null) {
    if (!clipRect) return;
    const { height, width, x, y } = clipRect;
    this.pdf.rect(x, y, width, height, null);
    this.pdf.clip();
    this.pdf.discardPath();
  }
}

function getIsFiniteRect({
  cornerRadius,
  height,
  width,
  x,
  y,
}: Pick<DrawRectInstruction, 'cornerRadius' | 'height' | 'width' | 'x' | 'y'>) {
  return [cornerRadius, height, width, x, y].every(value => Number.isFinite(value));
}
